'use client';

import * as React from 'react';
import type { CSSProperties } from 'react';
import { cx } from '../utils';
import type { StyledProps, TintName } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export interface ThumbImage {
  cutout?: string;
  thumb?: string;
  hero?: string;
}

export type ThumbImageMode = 'cutout' | 'cover';

/* Bounded rest-prop allow-list (codex Card r1 lesson). The compile-
   time type narrows the API; `pickAllowed` filters at runtime to
   defend against typed-bag spread bypass. */
type AllowedRestKey = 'id' | 'title' | `data-${string}` | `aria-${string}` | 'lang' | 'dir';
type AllowedRest = Partial<Record<AllowedRestKey, unknown>>;
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface ThumbProps extends StyledProps, UnsafeStyleProps, AllowedRest {
  label?: string | number | null;
  size?: number;
  tint?: TintName;
  radius?: number;
  image?: ThumbImage;
  imageMode?: ThumbImageMode;
}

export const Thumb = React.forwardRef<HTMLDivElement, ThumbProps>(function Thumb(
  { label, size = 40, tint, radius, image, imageMode, className, unsafe_style, ...rest },
  ref,
) {
  const [failed, setFailed] = React.useState(false);
  const src = image?.cutout || image?.thumb || image?.hero;
  /* When the src changes (adopter swaps the image after a previous
     load failure), reset the failed flag so the new src renders the
     image branch instead of permanently sticking on fallback. Without
     this effect, `<Thumb image={{ cutout: brokenUrl }} />` followed
     by `<Thumb image={{ cutout: goodUrl }} />` on the SAME component
     instance would stay on the fallback letter — surprising behavior
     that adopter bug reports would never trace back to here. */
  React.useEffect(() => {
    setFailed(false);
  }, [src]);
  const mode: ThumbImageMode = imageMode ?? (image?.cutout ? 'cutout' : 'cover');

  const inline: CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
    ...(radius != null ? { borderRadius: `${radius}px` } : null),
    /* Floor the label's font size so a caller passing an unusually
       small `size` (or 0) still renders a readable initial instead of
       collapsing to 0px. */
    ...(label && !src ? { fontSize: `${Math.max(8, Math.round(size * 0.32))}px` } : null),
    ...unsafe_style,
  };

  const tintCls = tint ? `j-thumb-${tint}` : '';

  const restProps = pickAllowed(
    rest as Record<string, unknown>,
  ) as React.HTMLAttributes<HTMLDivElement>;

  if (src && !failed) {
    return (
      <div
        ref={ref}
        {...restProps}
        className={cx('j-thumb', tintCls, className)}
        style={inline}
        data-image-mode={mode}
      >
        {/* alt="" marks the image decorative when there's no
            label; when the consumer passes a label (person name,
            object name) we promote it to the alt text so the
            avatar carries an accessible name for screen readers. */}
        <img src={src} alt={label != null ? String(label) : ''} onError={() => setFailed(true)} />
      </div>
    );
  }
  /* Image failed / absent → initial-letter fallback. Wrap in
     role="img" aria-label={label} when a label is present so
     screen readers announce the thumb by name instead of
     silently skipping an abbreviation div. Decorative case
     (no label) keeps its empty accessible name. */
  const hasName = label != null && String(label) !== '';
  return (
    <div
      ref={ref}
      {...restProps}
      className={cx('j-thumb', tintCls, className)}
      style={inline}
      role={hasName ? 'img' : undefined}
      aria-label={hasName ? String(label) : undefined}
    >
      {hasName ? String(label).charAt(0) : ''}
    </div>
  );
});
